import { appStore } from '@/store';
import { logger } from '@/lib/logger';
import { getGlobals, getLocale, setAccessToken } from '@/lib/globals';
import { getParams } from '@/lib/utils/params';
import { getAddressMetaKey } from '@/lib/utils/miscUtils';
// @ts-expect-error - utils.js has no type declarations
import { validateAddressFields } from '@/components/Address/utils';
import type {
  AddressMetadataFragment,
  CheckoutBillingAddress,
  CheckoutShippingAddress,
  User,
} from '@/generated/graphql';

export const OB_ACTIONS = {
  CANCEL: 'cancel',
  ERROR: 'error',
  RETURN: 'return',
} as const;

export interface OBReturnParams {
  action: string;
  id: string;
  error: string;
}

type ServerAddress = (CheckoutBillingAddress | CheckoutShippingAddress) & {
  [key: string]: unknown;
};

/**
 * Shape returned by Severus getGuestUserData.js,
 * injected into window.__INITIAL_DATA__.buyerData.
 */
interface BuyerData {
  billingAddresses?: CheckoutBillingAddress[];
  buyer?: User;
  shippingAddresses?: CheckoutShippingAddress[];
}

type AddressMeta = AddressMetadataFragment;

const VALID_OB_ACTIONS = new Set<string>(Object.values(OB_ACTIONS));
const OB_RETURN_PARAMS = ['action', 'error', 'id', 'success'];

const base64Decode = (encoded: string): string => {
  try {
    return globalThis.atob(encoded); // eslint-disable-line no-undef
  } catch {
    return '';
  }
};

export const isOBSuccess = (p: OBReturnParams): boolean => {
  return p.action === OB_ACTIONS.RETURN && Boolean(p.id) && !p.error;
};
export const isOBCancel = (p: OBReturnParams): boolean => {
  return p.action === OB_ACTIONS.CANCEL;
};
export const isOBError = (p: OBReturnParams): boolean => {
  return (
    p.action === OB_ACTIONS.ERROR ||
    (p.action === OB_ACTIONS.RETURN && Boolean(p.error))
  );
};

export const OB_ERROR_KEYS = {
  AUTH: 'pageLevelError.openBankingAuthError',
  INTERNAL: 'pageLevelError.openBankingInternalError',
  TERMINAL: 'pageLevelError.openBankingTerminalError',
};

const OB_ERROR_CODE_TO_KEY: Record<string, string> = {
  AUTHENTICATION_ERROR: OB_ERROR_KEYS.AUTH,
  TERMINAL_ERROR: OB_ERROR_KEYS.TERMINAL,
  TRANSIENT_ERROR: OB_ERROR_KEYS.INTERNAL,
};

const showOpenBankingError = (errorKey = OB_ERROR_KEYS.TERMINAL): void => {
  const { ui: uiActions } = appStore.getActions();
  uiActions.setPageWarningMessage(errorKey);
  globalThis.scrollTo(0, 0); // eslint-disable-line no-undef
};

export const getOpenBankingReturnParams = (): OBReturnParams | null => {
  try {
    const params = getParams();

    const id = String(params.id || '');
    const success = String(params.success || '') && id;
    const error = String(params.error || '');
    const action = String(params.action || '');

    if (!VALID_OB_ACTIONS.has(action) && !success) {
      return null;
    }

    if (success) {
      return {
        action: OB_ACTIONS.RETURN,
        error: '',
        id,
      };
    }

    return {
      action: error ? OB_ACTIONS.ERROR : OB_ACTIONS.CANCEL,
      error: error ? base64Decode(error) : '',
      id,
    };
  } catch (err: unknown) {
    logger.error('open_banking_get_return_params_failed', {
      message: (err as Error)?.message,
    });

    return null;
  }
};

export const cleanOpenBankingParams = (): void => {
  try {
    const url = new URL(globalThis.location.href); // eslint-disable-line no-undef

    OB_RETURN_PARAMS.forEach(p => url.searchParams.delete(p));
    globalThis.history.replaceState({}, '', url.toString()); // eslint-disable-line no-undef
    (getParams as unknown as { reset: () => void }).reset();
  } catch (err: unknown) {
    logger.error('open_banking_clean_params_failed', {
      message: (err as Error)?.message,
    });
  }
};

const restoreAddress = (
  addresses: ServerAddress[],
  addressMetadataByKey: Record<string, AddressMeta> | undefined,
  addressType: 'billingAddress' | 'shippingAddress',
): ServerAddress | null => {
  if (!addressMetadataByKey || !addresses.length) {
    return null;
  }

  const selectedAddress = addresses.find(a => a.isSelected) || addresses[0];
  if (!selectedAddress) {
    return null;
  }

  const addressCountry = selectedAddress.country;
  if (!addressCountry) {
    logger.warn('open_banking_restore_address_missing_country', {
      addressType,
    });
    return null;
  }

  const localeCountry = getLocale().country;
  if (localeCountry && localeCountry !== addressCountry) {
    logger.info('open_banking_restore_address_country_mismatch', {
      addressCountry,
      addressType,
      localeCountry,
    });
  }

  const addressMeta = addressMetadataByKey[getAddressMetaKey(addressCountry)];
  if (!addressMeta) {
    logger.warn('open_banking_restore_address_missing_meta', {
      addressCountry,
      addressType,
    });
    return null;
  }

  const validatedPayload: Record<string, { value?: string }> =
    validateAddressFields({
      address: selectedAddress,
      addressMeta,
      shouldUpdateValue: true,
    });

  const nonEmptyPayload = Object.fromEntries(
    Object.entries(validatedPayload).filter(([, data]) => data.value),
  );

  if (!Object.keys(nonEmptyPayload).length) {
    return null;
  }

  const { form: formActions } = appStore.getActions();
  formActions.updateAllAddressFieldsWithRestoredData({
    addressType,
    isBatchUpdate: true,
    payload: nonEmptyPayload,
  });

  return selectedAddress;
};

/**
 * Compares two addresses by their identifying fields to determine whether the
 * "ship to billing" checkbox should remain checked after restoration.
 */
const addressesMatch = (
  a: ServerAddress | null,
  b: ServerAddress | null,
): boolean => {
  if (!a || !b) {
    return false;
  }
  if (a.addressId && b.addressId) {
    return a.addressId === b.addressId;
  }
  const fields: Array<keyof ServerAddress> = [
    'line1',
    'line2',
    'city',
    'state',
    'postalCode',
    'country',
  ];
  return fields.every(field => (a[field] ?? '') === (b[field] ?? ''));
};

/**
 * Restores form fields from buyer data that Severus injected server-side
 */
export const restoreFormData = (): void => {
  const globals = getGlobals() as unknown as Record<string, unknown>;
  const buyerData = globals.buyerData as BuyerData | undefined;

  if (!buyerData) {
    logger.warn('open_banking_no_buyer_data_from_server');
    return;
  }

  const {
    billingAddresses = [],
    buyer = {},
    shippingAddresses = [],
  } = buyerData;
  const { form: formActions } = appStore.getActions();
  const { api: apiState } = appStore.getState();

  const setField = (
    field: 'email' | 'firstName' | 'lastName' | 'phone',
    value: string | null | undefined,
  ): void => {
    if (!value) {
      return;
    }
    formActions.updateNonAddressField({
      field,
      payload: { errorCode: '', isDirty: false, isValid: true, value },
    });
  };

  try {
    setField('email', buyer.email?.stringValue);
    setField('firstName', buyer.name?.givenName);
    setField('lastName', buyer.name?.familyName);

    const phone = buyer.phones?.[0];
    if (phone) {
      setField('phone', phone.number || phone.national);
    }
  } catch (err: unknown) {
    logger.error('open_banking_restore_buyer_fields_failed', {
      message: (err as Error)?.message,
    });
  }

  const addressMetadataByKey = apiState.griffinMetadata?.address;

  let selectedBilling: ServerAddress | null = null;
  let selectedShipping: ServerAddress | null = null;

  try {
    selectedBilling = restoreAddress(
      billingAddresses,
      addressMetadataByKey,
      'billingAddress',
    );
  } catch (err: unknown) {
    logger.error('open_banking_restore_billing_address_failed', {
      message: (err as Error)?.message,
    });
  }

  try {
    selectedShipping = restoreAddress(
      shippingAddresses,
      addressMetadataByKey,
      'shippingAddress',
    );
  } catch (err: unknown) {
    logger.error('open_banking_restore_shipping_address_failed', {
      message: (err as Error)?.message,
    });
  }

  if (selectedShipping && !addressesMatch(selectedBilling, selectedShipping)) {
    formActions.updateShipToBilling(false);
  }
};

/**
 * Main entry point: detects Open Banking return, cleans URL params, restores form data
 * from server-injected buyer data.
 */
export const handleOpenBankingReturn = (): OBReturnParams | null => {
  try {
    const obParams = getOpenBankingReturnParams();

    if (!obParams) {
      return null;
    }

    const auth = getGlobals().user?.auth;
    if (auth?.access_token) {
      setAccessToken({ type: '', value: auth.access_token });
    }

    const fptiPayload = {
      ob_action: obParams.action,
      ob_error: obParams.error || '',
      state_name: 'open_banking',
    };

    switch (true) {
      case isOBSuccess(obParams):
        logger.fpti('GUEST_OB_RETURN_SUCCESS', {
          ...fptiPayload,
          ob_bank_id: obParams.id,
          transition_name: 'ob_return_success',
        });
        break;
      case isOBCancel(obParams):
        logger.fpti('GUEST_OB_RETURN_CANCEL', {
          ...fptiPayload,
          transition_name: 'ob_return_cancel',
        });
        break;
      case isOBError(obParams):
        logger.fpti('GUEST_OB_RETURN_ERROR', {
          ...fptiPayload,
          transition_name: 'ob_return_error',
        });
        showOpenBankingError(
          OB_ERROR_CODE_TO_KEY[obParams.error] || OB_ERROR_KEYS.TERMINAL,
        );
        break;
      default:
        break;
    }

    logger.metric('GUEST_SIGNUP.OPEN_BANKING.RETURN', {
      dimensions: {
        action: obParams.action,
        has_error: Boolean(obParams.error),
      },
    });

    cleanOpenBankingParams();
    restoreFormData();

    return obParams;
  } catch (err: unknown) {
    logger.error('open_banking_handle_return_failed', {
      message: (err as Error)?.message,
    });
    return null;
  }
};
